<template>
  <div :class="componentClasses">
    <div class="field-squares">
      <div v-for="n in props.level" :key="n" class="field-square" />
    </div>
    <p class="field-title-value">
      <template v-if="valueType === 'object' || valueType === 'array'">
        <span v-for="p in properParts" class="field-title-part">{{ p }}</span>
      </template>
      <span v-else>{{ lastPart }}</span>
      <slot />
    </p>
    <Button
      v-if="valueType === 'array'"
      size="3"
      spacing="tight"
      icon-before="fas fa-plus"
      @click="reconciliationStore.addItemAtPath(name)"
    >
      Add
    </Button>
    <Badge v-if="reconciliationStore.isPendingDelete(name)" status="danger">
      Marked for deletion
    </Badge>
    <Button
      v-if="arrayIndex !== undefined && arrayIndex !== null"
      size="3"
      spacing="tight"
      :icon-before="reconciliationStore.isPendingDelete(name) ? 'fas fa-check' : 'fas fa-times'"
      @click="reconciliationStore.toggleItemDeletion(props.name)"
    >
      {{ reconciliationStore.isPendingDelete(name) ? 'Restore' : 'Delete' }}
    </Button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Badge, Button } from '@stakeui';
import { prettifyMixedCase, ordinal_suffix_of } from '@frontend/shared/Util.js';
import { useReconciliationStore } from '@frontend/ui/stores/reconciliationStore.js'

const reconciliationStore = useReconciliationStore()

const props = defineProps({
  level: Number,
  name: String,
  valueType: String,
  type: String,
  arrayIndex: Number,
  isEmpty: Boolean,
})

function splitPath(path) {
  if (!path) {
    return [];
  }

  return path.split('.').flatMap(segment => {
    const parts = [];
    const bracketRegex = /\[[^\]]+\]/g;

    let base = segment.replace(bracketRegex, '');
    if (base) parts.push(base);

    const matches = segment.match(bracketRegex);
    if (matches) parts.push(...matches);

    return parts;
  });
}

const properParts = computed(() => {
  const parts = splitPath(props.name);

  return parts.map(p => {
    const match = p.match(/\[(\d+)\]/);

    if (match) {
      const index = parseInt(match[1], 10);
      // Convert 0 → 1, so [0] is "1st"
      return `${ordinal_suffix_of(index + 1)} entry`;
    }

    return prettifyMixedCase(p);
  });
});

const lastPart = computed(() => properParts.value.length > 0 ? properParts.value[properParts.value.length - 1] : '');

const componentClasses = computed(() => [
  'field-title',
  props.type && `type-${props.type}`,
  props.isEmpty && `is-empty`,
  props.arrayIndex !== undefined && props.arrayIndex !== null
    ? `is-array-item array-index-${props.arrayIndex % 3}`
    : ''
]);
</script>

<style scoped>
.field-group.is-top-level > .field-title {
  &.type-object,
  &.type-array {
    background-color: var(--stk-text-40);
    box-shadow: none;
    color: white;
    padding: 0.5em 0.75em;
    border-radius: 0.5em;
  }
}

.field-title {
  --first: #00c2b5;
  --second: #17BCF7;
  --third: #4872F9;
  --fourth: #B279FB;
  --fifth: #F480D5;
  --sixth: #F48080;
  color: inherit;
  display: flex;
  gap: 0.5em;
  flex-grow: 1;
  align-items: center;
  font-weight: 500;

  &.is-empty {
    padding: 0.5em 0.75em;
    border-radius: 0.5em;
    font-style: italic;
    color: var(--stk-text-weak);
  }

  &.type-object,
  &.type-array {
    padding: 0.5em 0.75em;
    border-radius: 0.5em;
    position: sticky;
    top: 0;
    z-index: 1;
    /* margin: 2px 0; */
  }

  &.type-object {
    background-color: var(--stk-text-90);
    color: var(--stk-text-30);
  }

  &.type-array {
    background-color: white;
    box-shadow: inset 0 0 0 1px var(--stk-border);
  }

  &.is-top-level {
    box-shadow: none;
  }

  .is-array-item + .field-object &,
  &.is-array-item {
    background-image: linear-gradient(rgb(255 255 255 / 90%), rgb(255 255 255 / 90%)),
    linear-gradient(var(--color), var(--color));
    color: var(--color);
  }

  .array-index-0 + .field-object &.type-object,
  &.array-index-0 {
    --color: var(--first);
  }

  .array-index-1 + .field-object &.type-object,
  &.array-index-1 {
    --color: var(--second);
  }

  .array-index-2 + .field-object &.type-object,
  &.array-index-2 {
    --color: var(--third);
  }

  .array-index-3 + .field-object &.type-object,
  &.array-index-3 {
    --color: var(--fourth);
  }

  .array-index-4 + .field-object &.type-object,
  &.array-index-4 {
    --color: var(--fifth);
  }

  .array-index-5 + .field-object &.type-object,
  &.array-index-5 {
    --color: var(--sixth);
  }
}

.field-title-value {
  display: flex;
  align-items: center;
  gap: 0.5em;
  flex-wrap: wrap;
  color: inherit;
  font-weight: inherit;
  flex-grow: 1;
}

.field-title-part {
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;
}

.field-title-part:not(:last-child) {
  &::after {
    content: "/";
    margin-left: 0.5em;
  }
}

.field-title:not(:hover) .field-title-part:not(:last-child) {
  opacity: 0.3;
}

.field-squares {
  display: none;
}

.field-square {
  height: 2rem;
  width: 1rem;
  flex-shrink: 0;
  position: relative;
  opacity: 0;

  &:last-child::before {
    content: "";
    background-color: black;
    height: 100%;
    width: 2px;
    left: calc(50% - 1px);
    top: 0;
    position: absolute;
  }
}
</style>
